import { createLlmGatewayKey } from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import { useDispatch, useLoader, useLoaderSuccess } from "@app/react";
import { useSelector } from "@app/react";
import { useCurrentUser, useValidator } from "@app/ui/hooks";
import { AiTermsOfServiceLink } from "@app/ui/shared/ai-terms";
import { Banner, BannerMessages } from "@app/ui/shared/banner";
import { Button } from "@app/ui/shared/button";
import { CopyTextButton } from "@app/ui/shared/copy";
import { Form, FormGroup } from "@app/ui/shared/form-group";
import { IconCheckCircle, IconKey } from "@app/ui/shared/icons";
import { CheckBox, Input } from "@app/ui/shared/input";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
} from "@app/ui/shared/modal";
import { ModalWrapper } from "@app/ui/shared/modal-portal";
import { createUserAgreement, selectCurrentUserId } from "@app/users";
import { handleValidator } from "@app/validator";
import { useState } from "react";
import {
  LlmGatewayKeySpendLimitField,
  spendLimitValidator,
} from "./spend-limit-field";

interface LlmGatewayKeyFormData {
  handle: string;
  spendLimit: number | undefined;
}

interface CreateLlmGatewayKeyModalProps {
  isOpen: boolean;
  orgSpendLimit: number;
  onClose: () => void;
  onSuccess: (token: string, gatewayUrl: string) => void;
}

export const CreateLlmGatewayKeyModal = ({
  isOpen,
  orgSpendLimit,
  onClose,
  onSuccess,
}: CreateLlmGatewayKeyModalProps) => {
  const dispatch = useDispatch();
  const orgId = useSelector(selectOrganizationSelectedId);
  const userId = useSelector(selectCurrentUserId);
  const [currentUser] = useCurrentUser();
  const [handle, setHandle] = useState("");
  const [spendLimit, setSpendLimit] = useState<number | undefined>();
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const validators = {
    handle: (data: LlmGatewayKeyFormData) => handleValidator(data.handle),
    spendLimit: (data: LlmGatewayKeyFormData) =>
      spendLimitValidator(data.spendLimit, orgSpendLimit),
  };
  const [errors, validate] = useValidator<
    LlmGatewayKeyFormData,
    typeof validators
  >(validators);

  const loader = useLoader(createLlmGatewayKey);

  // The API rejects creation for users who haven't acknowledged the PHI terms.
  const hasAcknowledgedPhi = currentUser.allowAiGatewayAccess || acceptedTerms;

  const handleClose = () => {
    setHandle("");
    setSpendLimit(undefined);
    onClose();
  };

  useLoaderSuccess(loader, () => {
    const { token = "", gatewayUrl = "" } = loader.meta;
    handleClose();
    onSuccess(token, gatewayUrl);
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate({ handle, spendLimit })) return;
    dispatch(createLlmGatewayKey({ handle, orgId, spendLimit }));
  };

  if (!isOpen) return null;

  return (
    <ModalWrapper>
      <Modal isOpen={isOpen} className="min-w-[600px]">
        <ModalHeading
          icon={<IconKey variant="sm" />}
          title="Create LLM Gateway Key"
          onClose={handleClose}
        />
        <Form onSubmit={onSubmit} noValidate>
          <ModalBody className="pb-0">
            <FormGroup
              label={
                <>
                  Handle <span className="text-red-500">*</span>
                </>
              }
              htmlFor="handle"
              className="mb-4"
              feedbackMessage={errors.handle}
              feedbackVariant={errors.handle ? "danger" : "default"}
            >
              <Input
                id="handle"
                name="handle"
                value={handle}
                onChange={(e) => setHandle(e.target.value)}
                placeholder="e.g., production-llm-key"
                maxLength={64}
              />
            </FormGroup>

            <LlmGatewayKeySpendLimitField
              id="spend-limit"
              value={spendLimit}
              onChange={setSpendLimit}
              orgSpendLimit={orgSpendLimit}
              error={errors.spendLimit}
              className="mb-4"
            />

            {!currentUser.allowAiGatewayAccess && (
              <CheckBox
                checked={acceptedTerms}
                disabled={acceptedTerms}
                onChange={(e) => {
                  if (!e.currentTarget.checked) return;
                  setAcceptedTerms(true);
                  dispatch(
                    createUserAgreement({
                      userId,
                      agreementType: "llm_phi_acknowledgement",
                      agreementVersion: "1",
                    }),
                  );
                }}
                labelClassName="ml-3"
                label={
                  <>
                    I have read and agree to the <AiTermsOfServiceLink /> and
                    understand that{" "}
                    <b>
                      a BAA is required before sending any PHI through the
                      gateway.
                    </b>
                  </>
                }
              />
            )}

            <BannerMessages className="mt-4" {...loader} />
          </ModalBody>
          <ModalFooter
            actions={[
              <Button key="cancel" variant="white" onClick={handleClose}>
                Cancel
              </Button>,
              <Button
                key="submit"
                type="submit"
                disabled={!hasAcknowledgedPhi || !handle.trim()}
                isLoading={loader.isLoading}
              >
                Create Key
              </Button>,
            ]}
          />
        </Form>
      </Modal>
    </ModalWrapper>
  );
};

interface LlmGatewayKeyTokenModalProps {
  isOpen: boolean;
  token: string;
  gatewayUrl: string;
  onClose: () => void;
}

export const LlmGatewayKeyTokenModal = ({
  isOpen,
  token,
  gatewayUrl,
  onClose,
}: LlmGatewayKeyTokenModalProps) => {
  if (!isOpen) return null;

  return (
    <ModalWrapper>
      <Modal isOpen={isOpen} className="min-w-[600px]">
        <ModalHeading
          icon={<IconCheckCircle variant="sm" color="#00633f" />}
          title="LLM Gateway Key Created"
          onClose={onClose}
        />
        <ModalBody>
          <Banner variant="warning" className="mb-4">
            Be sure to copy your token now, as you will not be able to access it
            again once you close this window.
          </Banner>

          <FormGroup label="Token" htmlFor="llm-gateway-token" className="mb-4">
            <div className="flex items-center gap-2">
              <Input
                id="llm-gateway-token"
                type="text"
                value={token}
                readOnly
                className="flex-1 font-mono text-sm text-center"
              />
              <CopyTextButton text={token} />
            </div>
          </FormGroup>

          <FormGroup label="Endpoint URL" htmlFor="endpoint-url">
            <div className="flex items-center gap-2">
              <Input
                id="endpoint-url"
                type="text"
                value={gatewayUrl}
                readOnly
                className="flex-1 font-mono text-sm text-center"
              />
              <CopyTextButton text={gatewayUrl} />
            </div>
          </FormGroup>
        </ModalBody>
      </Modal>
    </ModalWrapper>
  );
};
